import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, wrap, damp, angTo } from './kit';
/* ============================================================
   ZENIT 天頂 —— BOSS・空中砲台
   ============================================================

   剪影：浮空環＋四具垂莢
   二階段：環開至 5.4 倍・護罩全開（紫→核心）

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* BOSS-02 ZENIT 天頂 —— 浮空砲艦。
   剪影：一個懸在空中的環，底下垂四個莢艙。與地面上任何東西都不會混。
   弱點只在蓄力時露出來 —— 這是「造型講機制」：護罩開＝可以打了。 */
function zenitPod(ang, GL){
  var g = new THREE.Group(); g.userData.bone = 1;
  g.position.set(Math.sin(ang)*1.78, -0.36, Math.cos(ang)*1.78);
  g.rotation.y = ang;
  g.add(mesh (G.cyl,  M.trim,  [0.20, 0.86, 0.20], [0,-0.30,0], [0.20,0,0]));
  var arm = new THREE.Group(); arm.userData.bone = 1; arm.position.y = -0.72; g.add(arm);
  arm.add(bevel(M.dark,  0.62, 0.54, 0.84, [0,-0.18,0]));
  arm.add(plate(M.armor, 0.70, 0.24, 0.62, [0,0.06,0.06], -0.24));
  arm.add(rib  (M.armor, 0.72, 0.08, 0.08, [0,0.20,0.20], -0.24));
  arm.add(mesh (G.cyl,   M.trim, [0.19, 0.86, 0.19], [0,-0.30, 0.50], [HP,0,0]));
  arm.add(mesh (G.tor,   GL,     [0.36,0.36,0.7], [0,-0.30, 0.92]));
  arm.add(mesh (G.box,   M.trim, [0.34,0.20,0.28], [0,-0.16,-0.44]));
  return {root:g, arm:arm, dead:0};
}
export function buildZenit(){
  var GL  = glowMat(TINT.vio.hex, 1.9);
  var GLC = glowMat(TINT.azu.hex, 2.2);
  var root = new THREE.Group();
  var body = new THREE.Group(); body.userData.bone = 1; body.position.y = 2.72; root.add(body);

  body.add(bevel(M.dark,  1.52, 0.82, 1.52, [0,0,0]));
  body.add(mesh (G.cone,  M.armor, [1.72, 0.84, 1.72], [0, 0.58, 0]));
  body.add(mesh (G.cone,  M.dark,  [1.34, 0.94, 1.34], [0,-0.64, 0], [Math.PI,0,0]));
  body.add(mesh (G.tor,   M.trim,  [2.10, 2.10, 0.9],  [0, 0.06, 0], [HP,0,0]));
  body.add(plate(M.armor, 0.34, 0.44, 1.30, [ 0.86, 0.20, 0], 0, -0.30, -0.20));
  body.add(plate(M.armor, 0.34, 0.44, 1.30, [-0.86, 0.20, 0], 0,  0.30,  0.20));
  body.add(mesh (G.cyl,   M.trim,  [0.28, 0.70, 0.28], [0, 0.96, 0]));
  body.add(mesh (G.oct,   GLC,     [0.30, 0.30, 0.30], [0, 1.34, 0]));

  /* 外環：自轉。六個環上模組讓「它在轉」在遠處也讀得到 */
  var ring = new THREE.Group(); ring.userData.bone = 1; body.add(ring);
  ring.add(mesh(G.tor, M.armor, [4.60, 4.60, 1.15], [0,0,0], [HP,0,0]));
  ring.add(mesh(G.tor, GL,      [3.82, 3.82, 0.55], [0,-0.08,0], [HP,0,0]));
  for (var i = 0; i < 6; i++){
    var a = i/6*Math.PI * 2, x = Math.sin(a)*2.26, z = Math.cos(a)*2.26;
    ring.add(bevel(M.dark,  0.54, 0.40, 0.76, [x, 0, z], [0, a, 0]));
    ring.add(plate(M.armor, 0.60, 0.14, 0.62, [x, 0.24, z], -0.20, a, 0));
    ring.add(mesh (G.box,   GL,  [0.30, 0.05, 0.05], [x*1.06, -0.16, z*1.06], [0, a, 0]));
  }

  /* 護罩三片：合起來把核心遮住，蓄力時掀開 */
  var sh = [];
  for (i = 0; i < 3; i++){
    var b = i/3*Math.PI * 2;
    var sg = new THREE.Group(); sg.userData.bone = 1; sg.rotation.y = b; body.add(sg);
    sg.add(plate(M.armor, 1.20, 1.10, 0.26, [0, 0.02, 0.86], -0.10, 0, 0));
    sg.add(rib  (M.armor, 1.24, 0.10, 0.10, [0, 0.52, 0.92], -0.10, 0, 0));
    sh.push(sg);
  }
  var core = mesh(G.oct, GLC, [1.00, 1.10, 1.00], [0, -0.06, 0]);
  core.userData.noMerge = 1; body.add(core);

  var pods = [zenitPod(0, GL), zenitPod(HP, GL), zenitPod(Math.PI, GL), zenitPod(-HP, GL)];
  for (i = 0; i < 4; i++) body.add(pods[i].root);

  var beam = new THREE.Mesh(G.cyl, new THREE.MeshBasicMaterial({
    color:0xdcb4ff, transparent:true, opacity:0, blending:THREE.AdditiveBlending, depthWrite:false}));
  beam.scale.set(0.1, 6, 0.1); beam.position.y = -3.4; beam.visible = false;
  beam.userData.noMerge = 1; body.add(beam);

  root.userData.rig = {body:body, ring:ring, sh:sh, core:core, pods:pods,
                       beam:beam, GL:GL, GLC:GLC, hoverY:2.72};
  return mergeBones(root);
}
function updZenitBase(rig, o, dt){
  rig.body.position.y = rig.hoverY + 0.20*Math.sin(o.t*0.9) - o.tel*0.30 + o.atk*0.10;
  rig.body.rotation.set(0.05*Math.sin(o.t*0.7) + o.atk*0.05, 0, 0.06*Math.sin(o.t*0.55));
  rig.ring.rotation.y += (0.35 + 1.9*o.tel) * dt;
  for (var i = 0; i < 3; i++){
    var open = o.tel*0.85 + o.atk*1.0;
    rig.sh[i].rotation.x = -open*0.9;
    rig.sh[i].position.y = open*0.34;
  }
  var ck = 1 + 0.6*o.tel + 0.9*o.atk;
  rig.core.scale.set(ck, ck*1.1, ck);
  rig.core.visible = o.tel > 0.05 || o.atk > 0.01 || o.broken;
  for (i = 0; i < 4; i++){
    var p = rig.pods[i];
    p.arm.rotation.x = p.dead ? 1.15 : (-0.10 - o.tel*0.16 + o.atk*0.26 +
                        0.08*Math.sin(o.t*1.3 + i));
    p.root.rotation.y = i/4*Math.PI * 2 + (p.dead ? 0 : angTo(0, wrap(o.aim - o.yaw), 0.6)*0.5);
    p.arm.visible = true;
  }
  rig.GL.emissiveIntensity  = rig.GL.userData.base  * (1 + 0.12*Math.sin(o.t*2.2) + 1.4*o.tel);
  rig.GLC.emissiveIntensity = rig.GLC.userData.base * (1 + 3.4*o.tel + 4.0*o.atk);
  rig.beam.visible = o.atk > 0.02;
  if (rig.beam.visible){
    var w = 0.9 * Math.sin(Math.min(1, o.atk)*Math.PI);
    rig.beam.scale.set(w, 6, w);
    rig.beam.material.opacity = 0.75 * Math.sin(Math.min(1, o.atk)*Math.PI);
  }
}
/* 原型 3072–3092 的補丁：WALZE / ZENIT 是最早做的兩隻，當時還沒有「階段」
   這個概念，所以二階段是**加上去**的而不是重寫。移植器把它折成
   base + wrapper 兩段（原型是在模組尾端重新指派同名變數，ESM 做不到）。 */
export function updZenit(rig, o, dt) {
  updZenitBase(rig, o, dt);
  rig.f2 = damp(rig.f2 === undefined ? 0 : rig.f2, o.p2 ? 1 : 0, 2.0, dt);
  var f = rig.f2, i;
  if (rig.ring) rig.ring.rotation.z += dt*3.4*f;
  if (rig.sh) for (i = 0; i < rig.sh.length; i++) rig.sh[i].rotation.x -= 0.90*f;
  if (rig.core) rig.core.scale.setScalar(1 + 0.85*f);
}
